import React from "react";
import PageHeaderWrapper from "../../../../components/PageHeaderWrapper";
import Request from "../../../../request";
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Button,
  Tabs,
  Table,
  Modal,
  Drawer,
  Tooltip,
  Popconfirm,
  message,
} from "antd";
import moment from "moment";
import styles from "./rawInquiryList.less";
import rightAuth from "../../../../components/_utils/userRights";
import ClickToCallDrawer from "../all/clickToCall";
import { RightsWrapper } from "../../../../components/_utils/utils";

const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

/* ---------------- Dropdown constants ---------------- */
const SOURCES = ["Website", "Google", "Facebook", "Instagram", "Meta Ads", "Walk-in", "Referral", "WhatsApp"];
const COUNTRIES = ["Canada", "Australia", "UK", "USA", "Ireland", "Germany"];
const RATINGS = ["Hot", "Warm", "Cold"];
/* Statuses a user can manually pick in the status drawer. "Moved" is a
   system-set status only reached via the Move action, so it's not in this list. */
const STATUSES = ["Active", "Dropped", "Future Leads"];
/* All statuses that can appear on a lead, including system-set ones - used for tabs. */
const ALL_STATUSES = ["Active", "Dropped", "Future Leads", "Moved"];

const SUB_RATINGS = {
  Active: ["Enrolled", "Open", "Not Applying Currently"],
  "Future Leads": [
    "Future Lead - Will process next year",
    "Next Intake",
    "Next Semester",
    "Waiting for IELTS",
    "Waiting for Passport",
    "Waiting for Result",
  ],
  /* "Lost" was merged into "Dropped" - they meant the same thing. */
  Dropped: [
    "Tourist Visa",
    "Not Interested",
    "Online Class Details Shared",
    "On Vision",
    "Not Connected",
    "Fake Lead",
    "RNR-1",
    "RNR-2",
    "RNR-3",
    "RNR-4",
    "RNR-5",
    "Not Eligible",
    "Plan Dropped",
    "Invalid Number",
    "Financial Issue",
    "Applied from Another Agent",
    "Work Permit",
    "Study Package",
    "Lost Lead - Mistakenly Registered",
    "Competitor Won",
    "Already Enrolled Elsewhere",
  ],
};

const STATUS_COLOR = {
  Active: "#16a34a",
  Dropped: "#ea580c",
  "Future Leads": "#2563eb",
  Moved: "#7c3aed",
};
const RATING_COLOR = { Hot: "#dc2626", Warm: "#ea580c", Cold: "#2563eb" };

/* Frontend row keys -> actual backend document fields, for server-side sorting */
const SORT_FIELD_MAP = {
  id: "id",
  name: "studentName",
  email: "emailId",
  source: "mode",
  counsellorName: "counsellorId",
  followupDate: "followUp.followUpDate",
  status: "rawInquiryStatus",
  rating: "rating",
};

/* Backend values aren't guaranteed to be strings (legacy imports can leave numbers/nulls in
   free-text fields), so every field the UI calls .toLowerCase()/.replace() on must be coerced. */
const str = (v) => (v == null ? "" : String(v));

/* Nested Mongo dates coming through the PHP backend can arrive as MongoDB extended JSON
   ({ "$date": "ISO..." } or { "$date": { "$numberLong": "169..." } }) instead of a plain
   ISO string, since only top-level dates get Carbon-cast before JSON serialization. */
function unwrapMongoDate(v) {
  if (v && typeof v === "object" && v.$date !== undefined) {
    return typeof v.$date === "object" ? Number(v.$date.$numberLong) : v.$date;
  }
  return v;
}

/* Maps a raw backend rawInquiry document onto the shape the UI works with */
function mapRecordToLead(record) {
  const rawFollowUpDate = record.followUp ? unwrapMongoDate(record.followUp.followUpDate) : null;
  const followUpMoment = rawFollowUpDate ? moment(rawFollowUpDate) : null;
  return {
    _id: record._id,
    id: record.id,
    name: str(record.studentName),
    email: str(record.emailId),
    phone: str(record.mobile),
    gender: record.gender || undefined,
    studentBranchId: record.studentBranchId || undefined,
    countryCode: record.countryCode || undefined,
    country: str(record.country),
    source: str(record.mode),
    counsellorId: record.counsellorId ? record.counsellorId._id : undefined,
    counsellorName: record.counsellorId ? str(record.counsellorId.name) : "-",
    followupDate: followUpMoment ? followUpMoment.format("YYYY-MM-DD") : "",
    followupTime: followUpMoment ? followUpMoment.format("HH:mm") : "10:00",
    status: str(record.rawInquiryStatus) || "Active",
    rating: str(record.rating),
    subRating: str(record.subRating),
    notes: str(record.notes),
    remarks: str(record.remarks),
  };
}

class RawInquiryList extends React.Component {
  state = {
    rows: [],
    total: 0,
    page: 1,
    pageSize: 15,
    sortField: undefined,
    sortOrder: undefined,
    allCounsellor: [],
    dealingPhoneCodes: [],
    statusSummary: {},
    counsellorSummary: [],
    totalCount: 0,
    todayCount: 0,
    weekCount: 0,
    monthCount: 0,
    loading: false,
    exportLoading: false,
    activeTab: "All",
    search: "",
    filters: {
      counsellorId: undefined,
      source: undefined,
      rating: undefined,
      status: undefined,
      subRating: undefined,
      date: null,
      createdDateRange: null,
    },

    addVisible: false,
    addForm: {},
    addSaving: false,

    editVisible: false,
    editLead: null,
    editForm: {},

    assignVisible: false,
    assignLead: null,
    assignCounsellorId: undefined,

    followupVisible: false,
    followupLead: null,
    followupForm: {},

    statusDrawerVisible: false,
    statusLead: null,
    drawerStatus: undefined,
    drawerSubRating: undefined,
    drawerRemarks: "",

    moveVisible: false,
    moveLead: null,
    moveTarget: undefined,
    moveSaving: false,

    showCallDrawer: false,
    recordForCall: {},
  };

  componentDidMount() {
    this.loadCounsellors();
    this.loadDealingPhoneCodes();
    this.loadLeads();
  }

  /* ---------------- Data loading ---------------- */
  loadCounsellors = async () => {
    let { data } = await Request.loadSimpleCounsellor({ results: 10000, atLevel: ["Counselling Desk"] });
    this.setState({ allCounsellor: data || [] });
  };

  loadDealingPhoneCodes = async () => {
    let response = await Request.getDealingCountries({ results: 100, sortField: "countryName", sortOrder: "ascend" });
    let dealingPhoneCodes = [];
    (response.data || []).forEach((c) => {
      if (c.countryPhoneCode && !dealingPhoneCodes.includes(c.countryPhoneCode)) {
        dealingPhoneCodes.push(c.countryPhoneCode);
      }
    });
    this.setState({ dealingPhoneCodes });
  };

  /* Filter/search/tab params shared by both the paginated list request and the full export */
  buildFilterParams = () => {
    const { activeTab, search, filters } = this.state;
    const params = {};
    const effectiveStatus = activeTab !== "All" ? activeTab : filters.status;
    if (effectiveStatus) params.rawInquiryStatus = effectiveStatus;
    if (search) params.keyword = search;
    if (filters.counsellorId) params.counsellorId = filters.counsellorId;
    if (filters.source) params.mode = JSON.stringify([filters.source]);
    if (filters.rating) params.rating = filters.rating;
    if (filters.subRating) params.subRating = filters.subRating;
    if (filters.date) {
      params.followUpDateFilter = JSON.stringify({
        $gte: filters.date.clone().startOf("day").toISOString(),
        $lte: filters.date.clone().endOf("day").toISOString(),
      });
    }
    if (filters.createdDateRange && filters.createdDateRange.length === 2) {
      params.createdDateFilter = JSON.stringify({
        $gte: filters.createdDateRange[0].clone().startOf("day").toISOString(),
        $lte: filters.createdDateRange[1].clone().endOf("day").toISOString(),
      });
    }
    return params;
  };

  loadLeads = async () => {
    this.setState({ loading: true });
    const { page, pageSize, sortField, sortOrder } = this.state;
    const params = { ...this.buildFilterParams(), results: pageSize, page };
    if (sortField) {
      params.sortField = SORT_FIELD_MAP[sortField] || sortField;
      params.sortOrder = sortOrder || "descend";
    }
    let response = await Request.getAllRawInquiryList(params);
    if (response && response.data) {
      this.setState({
        rows: response.data.map(mapRecordToLead),
        total: response.total || 0,
        statusSummary: response.statusSummary || {},
        counsellorSummary: response.counsellorSummary || [],
        totalCount: response.totalCount || 0,
        todayCount: response.todayCount || 0,
        weekCount: response.weekCount || 0,
        monthCount: response.monthCount || 0,
        loading: false,
      });
    } else {
      this.setState({ loading: false });
      message.error("Unable to load leads");
    }
  };

  /* ---------------- Search / Filters / Tabs ---------------- */
  onTabChange = (activeTab) => {
    this.setState({ activeTab, page: 1 }, () => this.loadLeads());
  };

  handleSearch = (value) => {
    this.setState({ search: value.trim(), page: 1 }, () => this.loadLeads());
  };

  handleFilterChange = (key, value) => {
    this.setState(
      (prevState) => {
        const newFilters = { ...prevState.filters, [key]: value };
        if (key === "status") {
          const allowed = value ? SUB_RATINGS[value] || [] : Object.values(SUB_RATINGS).flat();
          if (newFilters.subRating && !allowed.includes(newFilters.subRating)) {
            newFilters.subRating = undefined;
          }
        }
        return { filters: newFilters, page: 1 };
      },
      () => this.loadLeads()
    );
  };

  clearFilters = () => {
    this.setState(
      {
        search: "",
        page: 1,
        filters: {
          counsellorId: undefined,
          source: undefined,
          rating: undefined,
          status: undefined,
          subRating: undefined,
          date: null,
          createdDateRange: null,
        },
      },
      () => {
        this.loadLeads();
        message.info("Filters cleared");
      }
    );
  };

  handleTableChange = (pagination, tableFilters, sorter) => {
    this.setState(
      {
        page: pagination.current,
        pageSize: pagination.pageSize,
        sortField: sorter && sorter.order ? sorter.field : undefined,
        sortOrder: sorter && sorter.order ? sorter.order : undefined,
      },
      () => this.loadLeads()
    );
  };

  /* ---------------- Refresh ---------------- */
  refresh = async () => {
    await this.loadLeads();
    message.success("Lead pipeline is up to date");
  };

  /* ---------------- Export (full filtered set, via backend) ---------------- */
  exportExcel = async () => {
    this.setState({ exportLoading: true });
    let response = await Request.exportRawInquiryToExcel({ ...this.buildFilterParams(), results: 100000 });
    this.setState({ exportLoading: false });
    if (response && response.success && response.url) {
      window.open(response.url);
      message.success("Export ready");
    } else {
      message.error("No leads to export or something went wrong");
    }
  };

  /* ---------------- Add lead modal ---------------- */
  openAdd = () => {
    this.setState({
      addVisible: true,
      addForm: { name: "", email: "", phone: "", countryCode: undefined, country: undefined, source: undefined, counsellorId: undefined, rating: undefined, notes: "" },
    });
  };
  closeAdd = () => this.setState({ addVisible: false, addForm: {} });
  saveAdd = async () => {
    const { addForm } = this.state;
    const { name, email, phone } = addForm;
    if (!name || !email || !phone) {
      message.error("Name, email and phone are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.error("Enter a valid email address.");
      return;
    }
    this.setState({ addSaving: true });
    let response = await Request.addRawInquiry({
      studentName: name,
      emailId: email,
      mobile: phone,
      countryCode: addForm.countryCode,
      gender: "Not Specified",
      country: addForm.country,
      mode: addForm.source,
      counsellorId: addForm.counsellorId,
      rating: addForm.rating,
      notes: addForm.notes,
    });
    this.setState({ addSaving: false });
    if (response && response.success) {
      message.success(response.message || name + " was added to the pipeline.");
      this.closeAdd();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Edit modal ---------------- */
  openEdit = (lead) => {
    this.setState({
      editVisible: true,
      editLead: lead,
      editForm: { ...lead },
    });
  };
  closeEdit = () => this.setState({ editVisible: false, editLead: null, editForm: {} });
  saveEdit = async () => {
    const { editLead, editForm } = this.state;
    const { name, email, phone } = editForm;
    if (!name || !email || !phone) {
      message.error("Name, email and phone are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.error("Enter a valid email address.");
      return;
    }
    let response = await Request.updateRawInquiry({
      _id: editLead._id,
      studentName: name,
      emailId: email,
      mobile: phone,
      countryCode: editForm.countryCode,
      country: editForm.country,
      mode: editForm.source,
      counsellorId: editForm.counsellorId,
      rating: editForm.rating,
      notes: editForm.notes,
    });
    if (response && !response.error) {
      message.success(name + "'s details were saved.");
      this.closeEdit();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Assign counsellor modal ---------------- */
  openAssign = (lead) => {
    this.setState({ assignVisible: true, assignLead: lead, assignCounsellorId: lead.counsellorId });
  };
  closeAssign = () => this.setState({ assignVisible: false, assignLead: null, assignCounsellorId: undefined });
  saveAssign = async () => {
    const { assignLead, assignCounsellorId } = this.state;
    if (!assignCounsellorId) {
      message.error("Please select a counsellor");
      return;
    }
    let response = await Request.saveCounsellor({ _id: assignLead._id, counsellorId: assignCounsellorId });
    if (response && response.success) {
      message.success(response.message);
      this.closeAssign();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Followup modal ---------------- */
  openFollowup = (lead) => {
    this.setState({
      followupVisible: true,
      followupLead: lead,
      followupForm: {
        date: lead.followupDate ? moment(lead.followupDate) : moment(),
        time: lead.followupTime ? moment(lead.followupTime, "HH:mm") : moment("10:00", "HH:mm"),
        remarks: "",
        next: null,
      },
    });
  };
  closeFollowup = () => this.setState({ followupVisible: false, followupLead: null, followupForm: {} });
  saveFollowup = async () => {
    const { followupLead, followupForm } = this.state;
    const { date, time, remarks, next } = followupForm;
    if (!date || !time) {
      message.error("Date and time are required.");
      return;
    }
    const nextMoment = (next || date).clone().set({ hour: time.hour(), minute: time.minute(), second: 0 });
    let response = await Request.updateRawInquiry({
      _id: followupLead._id,
      followUp: {
        followUpDate: nextMoment.toDate(),
        details: remarks && remarks.trim() ? remarks.trim() : followupLead.remarks,
        followUpType: "Follow Up",
      },
    });
    if (response && !response.error) {
      message.success(followupLead.name + " · next touchpoint on " + nextMoment.format("DD MMM YYYY") + ".");
      this.closeFollowup();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Status drawer (dynamic sub-rating) ---------------- */
  openStatusDrawer = (lead) => {
    this.setState({
      statusDrawerVisible: true,
      statusLead: lead,
      drawerStatus: lead.status,
      drawerSubRating: lead.subRating,
      drawerRemarks: "",
    });
  };
  closeStatusDrawer = () =>
    this.setState({
      statusDrawerVisible: false,
      statusLead: null,
      drawerStatus: undefined,
      drawerSubRating: undefined,
      drawerRemarks: "",
    });
  pickDrawerStatus = (status) => {
    const opts = SUB_RATINGS[status] || [];
    this.setState((prevState) => ({
      drawerStatus: status,
      drawerSubRating: opts.includes(prevState.drawerSubRating) ? prevState.drawerSubRating : opts[0],
    }));
  };
  saveStatusDrawer = async () => {
    const { statusLead, drawerStatus, drawerSubRating, drawerRemarks } = this.state;
    let response = await Request.updateRawInquiry({
      _id: statusLead._id,
      rawInquiryStatus: drawerStatus,
      subRating: drawerSubRating,
      remarks: drawerRemarks && drawerRemarks.trim() ? drawerRemarks.trim() : statusLead.remarks,
    });
    if (response && !response.error) {
      message.success(statusLead.name + " → " + drawerStatus + " · " + drawerSubRating);
      this.closeStatusDrawer();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Click to Call ---------------- */
  openCallDrawer = (lead) => {
    this.setState({
      showCallDrawer: true,
      recordForCall: {
        _id: lead._id,
        id: lead.id,
        studentName: lead.name,
        mobile: lead.phone,
        emailId: lead.email,
        gender: lead.gender,
        countryCode: lead.countryCode,
        studentBranchId: { _id: lead.studentBranchId || null },
      },
    });
  };
  closeCallDrawer = () => {
    this.setState({ showCallDrawer: false, recordForCall: {} }, () => this.loadLeads());
  };

  /* ---------------- Move (to Inquiry or Visitor Visa) ---------------- */
  openMove = (lead) => {
    this.setState({ moveVisible: true, moveLead: lead, moveTarget: undefined });
  };
  closeMove = () => this.setState({ moveVisible: false, moveLead: null, moveTarget: undefined });
  saveMove = async () => {
    const { moveLead, moveTarget } = this.state;
    if (!moveTarget) {
      message.error("Please choose where to move this lead.");
      return;
    }
    this.setState({ moveSaving: true });
    let response = await Request.moveRawInquiry({ _id: moveLead._id, target: moveTarget });
    this.setState({ moveSaving: false });
    if (response && response.success) {
      message.success(
        moveLead.name + " moved to " + (moveTarget === "visitorVisa" ? "Visitor Visa" : "Inquiry") + "."
      );
      this.closeMove();
      this.loadLeads();
    } else {
      message.error((response && response.message) || "Try again");
    }
  };

  /* ---------------- Delete ---------------- */
  deleteLead = async (lead) => {
    let response = await Request.deleteRawInquiry({ _id: lead._id });
    if (response && !response.error) {
      message.success(lead.name + " was removed from the pipeline.");
      this.loadLeads();
    } else {
      message.error("Try again");
    }
  };

  render() {
    const {
      rows,
      total,
      page,
      pageSize,
      allCounsellor,
      dealingPhoneCodes,
      statusSummary,
      counsellorSummary,
      totalCount,
      todayCount,
      weekCount,
      monthCount,
      loading,
      exportLoading,
      activeTab,
      search,
      filters,
      addVisible,
      addForm,
      addSaving,
      editVisible,
      editForm,
      assignVisible,
      assignLead,
      assignCounsellorId,
      followupVisible,
      followupForm,
      statusDrawerVisible,
      statusLead,
      drawerStatus,
      drawerSubRating,
      drawerRemarks,
      moveVisible,
      moveLead,
      moveTarget,
      moveSaving,
      showCallDrawer,
      recordForCall,
    } = this.state;

    const subRatingFilterOptions = filters.status ? SUB_RATINGS[filters.status] || [] : Object.values(SUB_RATINGS).flat();

    const statCards = [
      { label: "Active", value: statusSummary["Active"] || 0, color: STATUS_COLOR.Active, tab: "Active" },
      { label: "Dropped", value: statusSummary["Dropped"] || 0, color: STATUS_COLOR.Dropped, tab: "Dropped" },
      { label: "Future Leads", value: statusSummary["Future Leads"] || 0, color: STATUS_COLOR["Future Leads"], tab: "Future Leads" },
      { label: "Moved", value: statusSummary["Moved"] || 0, color: STATUS_COLOR.Moved, tab: "Moved" },
    ];

    const leadVolumeCards = [
      { label: "Total", value: totalCount, color: "#2563eb" },
      { label: "Today", value: todayCount, color: "#16a34a" },
      { label: "This Week", value: weekCount, color: "#ea580c" },
      { label: "This Month", value: monthCount, color: "#7c3aed" },
    ];

    const columns = [
      {
        title: "Action",
        key: "operation",
        fixed: "left",
        width: 260,
        render: (text, record) => (
          <div>
            <Tooltip title="Assign Counsellor">
              <Button className={styles.btn} size="small" shape="circle" icon="user" onClick={() => this.openAssign(record)} />
            </Tooltip>
            <Tooltip title="Update Status">
              <Button className={styles.btn} size="small" shape="circle" icon="sync" onClick={() => this.openStatusDrawer(record)} />
            </Tooltip>
            <Tooltip title="Edit">
              <Button className={styles.btn} size="small" shape="circle" icon="edit" onClick={() => this.openEdit(record)} />
            </Tooltip>
            <Tooltip title="Notes">
              <Button className={styles.btn} size="small" shape="circle" icon="calendar" onClick={() => this.openFollowup(record)} />
            </Tooltip>
            {record.status !== "Moved" && (
              <Tooltip title="Move">
                <Button className={styles.btn} size="small" shape="circle" icon="swap" onClick={() => this.openMove(record)} />
              </Tooltip>
            )}
            <RightsWrapper myRight={"rawInquiry.delete"}>
              <Tooltip title="Delete">
                <Popconfirm title={`Delete ${record.name}?`} onConfirm={() => this.deleteLead(record)} okText="Yes" cancelText="No">
                  <Button className={styles.btn} size="small" shape="circle" icon="delete" />
                </Popconfirm>
              </Tooltip>
            </RightsWrapper>
          </div>
        ),
      },
      {
        title: "VID",
        dataIndex: "id",
        key: "id",
        width: 80,
        sorter: true,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 170,
        sorter: true,
        render: (text, record) => (
          <div>
            <b>{text}</b>
            <div className={styles.mutedText}>{record.country}</div>
          </div>
        ),
      },
      { title: "Email", dataIndex: "email", key: "email", width: 200, sorter: true },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 140,
        render: (val, record) =>
          val && val.length >= 10 && rightAuth.checkRights("ShowClickToCall.view") ? (
            <Tooltip title="Start Call">
              <Button type="primary" size="small" onClick={() => this.openCallDrawer(record)}>
                Click To Call
              </Button>
            </Tooltip>
          ) : (
            val
          ),
      },
      { title: "Source", dataIndex: "source", key: "source", width: 110, sorter: true },
      {
        title: "Counsellor",
        dataIndex: "counsellorName",
        key: "counsellorName",
        width: 130,
        sorter: true,
      },
      {
        title: "Followup Date",
        dataIndex: "followupDate",
        key: "followupDate",
        width: 160,
        sorter: true,
        render: (val, record) => {
          if (!val) return <span className={styles.mutedText}>-</span>;
          const today = moment().format("YYYY-MM-DD");
          const workable = record.status === "Active" || record.status === "Future Leads";
          const overdue = workable && val < today;
          const dueToday = workable && val === today;
          return (
            <span className={overdue ? styles.overdueText : dueToday ? styles.dueTodayText : ""}>
              {moment(val).format("DD MMM YYYY")}
              {overdue ? " · Overdue" : dueToday ? " · Today" : ""}
            </span>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        sorter: true,
        render: (val) => (
          <span className={styles.badgePill} style={{ background: STATUS_COLOR[val] }}>
            {val}
          </span>
        ),
      },
      {
        title: "Rating",
        dataIndex: "rating",
        key: "rating",
        width: 90,
        sorter: true,
        render: (val) =>
          val ? (
            <span className={styles.badgePill} style={{ background: RATING_COLOR[val] || "#8c8c8c" }}>
              {val}
            </span>
          ) : (
            <span className={styles.mutedText}>-</span>
          ),
      },
      {
        title: "Sub Rating",
        dataIndex: "subRating",
        key: "subRating",
        width: 200,
        render: (val) =>
          val ? (
            <Tooltip title={val}>
              <span className={styles.subRatingPill}>{val}</span>
            </Tooltip>
          ) : (
            <span className={styles.mutedText}>-</span>
          ),
      },
    ];

    return (
      <PageHeaderWrapper title={`Raw Inquiries : ${total}`}>
        {showCallDrawer ? (
          <ClickToCallDrawer record={recordForCall} type="rawInquiry" onClose={this.closeCallDrawer} />
        ) : null}

        {/* ==================== TOTAL LEADS ==================== */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          {leadVolumeCards.map((s) => (
            <Col xs={12} sm={6} key={s.label}>
              <Card bordered={true}>
                <div className={styles.mutedText}>{s.label}</div>
                <div className={styles.statValue} style={{ color: s.color }}>
                  {s.value}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ==================== COUNSELLOR OVERVIEW ==================== */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          {counsellorSummary.map((c) => (
            <Col xs={24} sm={12} md={8} key={c.counsellorId}>
              <Card bordered={true}>
                <b>{c.name}</b>
                <div className={styles.mutedText}>Counsellor</div>
                <Row style={{ marginTop: 10 }}>
                  <Col span={12}>
                    <div className={styles.metricValue}>{c.assigned}</div>
                    <div className={styles.mutedText}>Assigned Leads</div>
                  </Col>
                  <Col span={12}>
                    <div className={styles.metricValueWarn}>{c.pending}</div>
                    <div className={styles.mutedText}>Pending Followups</div>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ==================== LEAD SUMMARY ==================== */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          {statCards.map((s) => (
            <Col xs={12} sm={8} md={4} key={s.label}>
              <Card bordered={true} className={styles.statCard} onClick={() => this.onTabChange(s.tab)}>
                <div className={styles.mutedText}>{s.label}</div>
                <div className={styles.statValue} style={{ color: s.color }}>
                  {s.value}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ==================== SEARCH & FILTERS ==================== */}
        <Card bordered={true} style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Search
                placeholder="Search leads by name, phone or email…"
                defaultValue={search}
                onSearch={this.handleSearch}
                allowClear
                enterButton
              />
            </Col>
          </Row>

          <Row gutter={[12, 12]} align="bottom">
            <Col xs={24} sm={12} md={8} lg={4}>
              <label className={styles.filterLabel}>Counsellor</label>
              <Select
                placeholder="All Counsellors"
                style={{ width: "100%", marginTop: 4 }}
                value={filters.counsellorId}
                onChange={(v) => this.handleFilterChange("counsellorId", v)}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {allCounsellor.map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <label className={styles.filterLabel}>Source</label>
              <Select
                placeholder="All Sources"
                style={{ width: "100%", marginTop: 4 }}
                value={filters.source}
                onChange={(v) => this.handleFilterChange("source", v)}
                allowClear
              >
                {SOURCES.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={3}>
              <label className={styles.filterLabel}>Rating</label>
              <Select
                placeholder="All Ratings"
                style={{ width: "100%", marginTop: 4 }}
                value={filters.rating}
                onChange={(v) => this.handleFilterChange("rating", v)}
                allowClear
              >
                {RATINGS.map((r) => (
                  <Option key={r} value={r}>
                    {r}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={3}>
              <label className={styles.filterLabel}>Status</label>
              <Select
                placeholder="All Statuses"
                style={{ width: "100%", marginTop: 4 }}
                value={filters.status}
                onChange={(v) => this.handleFilterChange("status", v)}
                allowClear
              >
                {ALL_STATUSES.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={4}>
              <label className={styles.filterLabel}>Sub Rating</label>
              <Select
                placeholder="All Sub Ratings"
                style={{ width: "100%", marginTop: 4 }}
                value={filters.subRating}
                onChange={(v) => this.handleFilterChange("subRating", v)}
                allowClear
              >
                {subRatingFilterOptions.map((sr) => (
                  <Option key={sr} value={sr}>
                    {sr}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={3}>
              <label className={styles.filterLabel}>Followup Date</label>
              <DatePicker
                style={{ width: "100%", marginTop: 4 }}
                value={filters.date}
                onChange={(d) => this.handleFilterChange("date", d)}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label className={styles.filterLabel}>Lead Created Date</label>
              <RangePicker
                style={{ width: "100%", marginTop: 4 }}
                value={filters.createdDateRange}
                onChange={(dates) => this.handleFilterChange("createdDateRange", dates && dates.length ? dates : null)}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={3}>
              <label className={styles.filterLabel}>&nbsp;</label>
              <Button onClick={this.clearFilters} style={{ width: "100%", marginTop: 4 }}>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* ==================== TABS & TABLE ==================== */}
        <Card bordered={true}>
          <Row style={{ marginBottom: 10 }} gutter={[12, 12]}>
            <Col xs={24} lg={12}>
              <Tabs activeKey={activeTab} onChange={this.onTabChange} type="card">
                <TabPane tab={`All (${totalCount})`} key="All" />
                <TabPane tab={`Active (${statusSummary["Active"] || 0})`} key="Active" />
                <TabPane tab={`Dropped (${statusSummary["Dropped"] || 0})`} key="Dropped" />
                <TabPane tab={`Future Leads (${statusSummary["Future Leads"] || 0})`} key="Future Leads" />
                <TabPane tab={`Moved (${statusSummary["Moved"] || 0})`} key="Moved" />
              </Tabs>
            </Col>
            <Col xs={24} lg={12} style={{ textAlign: "right" }}>
              <Button type="primary" style={{ marginRight: 10 }} icon="plus" onClick={this.openAdd}>
                Add Lead
              </Button>
              <Button style={{ marginRight: 10 }} icon="reload" loading={loading} onClick={this.refresh}>
                Refresh
              </Button>
              <Button icon="download" loading={exportLoading} onClick={this.exportExcel}>
                Export Excel
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: 1400 }}
            onChange={this.handleTableChange}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "15", "25", "50"],
            }}
          />
        </Card>

        {/* ==================== ADD LEAD MODAL ==================== */}
        <Modal
          title="Add Lead"
          visible={addVisible}
          onCancel={this.closeAdd}
          onOk={this.saveAdd}
          okText="Save lead"
          confirmLoading={addSaving}
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <label className={styles.filterLabel}>Name</label>
              <Input
                value={addForm.name}
                onChange={(e) => this.setState({ addForm: { ...addForm, name: e.target.value } })}
              />
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Email</label>
              <Input
                value={addForm.email}
                onChange={(e) => this.setState({ addForm: { ...addForm, email: e.target.value } })}
              />
            </Col>
            <Col span={4}>
              <label className={styles.filterLabel}>Code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="+91"
                showSearch
                allowClear
                value={addForm.countryCode}
                onChange={(v) => this.setState({ addForm: { ...addForm, countryCode: v } })}
              >
                {dealingPhoneCodes.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label className={styles.filterLabel}>Phone</label>
              <Input
                value={addForm.phone}
                onChange={(e) => this.setState({ addForm: { ...addForm, phone: e.target.value } })}
              />
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Country</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Country"
                allowClear
                value={addForm.country}
                onChange={(v) => this.setState({ addForm: { ...addForm, country: v } })}
              >
                {COUNTRIES.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Source</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Source"
                allowClear
                value={addForm.source}
                onChange={(v) => this.setState({ addForm: { ...addForm, source: v } })}
              >
                {SOURCES.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Counsellor</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Counsellor"
                allowClear
                showSearch
                optionFilterProp="children"
                value={addForm.counsellorId}
                onChange={(v) => this.setState({ addForm: { ...addForm, counsellorId: v } })}
              >
                {allCounsellor.map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Rating</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Rating"
                allowClear
                value={addForm.rating}
                onChange={(v) => this.setState({ addForm: { ...addForm, rating: v } })}
              >
                {RATINGS.map((r) => (
                  <Option key={r} value={r}>
                    {r}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              <label className={styles.filterLabel}>Notes</label>
              <Input.TextArea
                rows={3}
                placeholder="Add internal notes about this lead…"
                value={addForm.notes}
                onChange={(e) => this.setState({ addForm: { ...addForm, notes: e.target.value } })}
              />
            </Col>
          </Row>
        </Modal>

        {/* ==================== EDIT MODAL ==================== */}
        <Modal
          title={editForm.id ? `Edit Lead #${editForm.id} · ${editForm.name || ""}` : "Edit Lead"}
          visible={editVisible}
          onCancel={this.closeEdit}
          onOk={this.saveEdit}
          okText="Save changes"
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <label className={styles.filterLabel}>Name</label>
              <Input
                value={editForm.name}
                onChange={(e) => this.setState({ editForm: { ...editForm, name: e.target.value } })}
              />
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Email</label>
              <Input
                value={editForm.email}
                onChange={(e) => this.setState({ editForm: { ...editForm, email: e.target.value } })}
              />
            </Col>
            <Col span={4}>
              <label className={styles.filterLabel}>Code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="+91"
                showSearch
                allowClear
                value={editForm.countryCode}
                onChange={(v) => this.setState({ editForm: { ...editForm, countryCode: v } })}
              >
                {dealingPhoneCodes.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <label className={styles.filterLabel}>Phone</label>
              <Input
                value={editForm.phone}
                onChange={(e) => this.setState({ editForm: { ...editForm, phone: e.target.value } })}
              />
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Country</label>
              <Select
                style={{ width: "100%" }}
                value={editForm.country}
                onChange={(v) => this.setState({ editForm: { ...editForm, country: v } })}
              >
                {COUNTRIES.map((c) => (
                  <Option key={c} value={c}>
                    {c}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Source</label>
              <Select
                style={{ width: "100%" }}
                value={editForm.source}
                onChange={(v) => this.setState({ editForm: { ...editForm, source: v } })}
              >
                {SOURCES.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Counsellor</label>
              <Select
                style={{ width: "100%" }}
                value={editForm.counsellorId}
                onChange={(v) => this.setState({ editForm: { ...editForm, counsellorId: v } })}
              >
                {allCounsellor.map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Rating</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Rating"
                allowClear
                value={editForm.rating || undefined}
                onChange={(v) => this.setState({ editForm: { ...editForm, rating: v } })}
              >
                {RATINGS.map((r) => (
                  <Option key={r} value={r}>
                    {r}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              <label className={styles.filterLabel}>Notes</label>
              <Input.TextArea
                rows={3}
                placeholder="Add internal notes about this lead…"
                value={editForm.notes}
                onChange={(e) => this.setState({ editForm: { ...editForm, notes: e.target.value } })}
              />
            </Col>
          </Row>
        </Modal>

        {/* ==================== ASSIGN COUNSELLOR MODAL ==================== */}
        <Modal
          title="Assign Counsellor"
          visible={assignVisible}
          onCancel={this.closeAssign}
          onOk={this.saveAssign}
          okText="Save assignment"
        >
          {assignLead ? (
            <div>
              <p className={styles.mutedText}>
                {assignLead.name} · currently with {assignLead.counsellorName}
              </p>
              <Select
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                value={assignCounsellorId}
                onChange={(v) => this.setState({ assignCounsellorId: v })}
              >
                {allCounsellor.map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </div>
          ) : null}
        </Modal>

        {/* ==================== NOTES MODAL ==================== */}
        <Modal
          title="Notes"
          visible={followupVisible}
          onCancel={this.closeFollowup}
          onOk={this.saveFollowup}
          okText="Save note"
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <label className={styles.filterLabel}>Date</label>
              <DatePicker
                style={{ width: "100%" }}
                value={followupForm.date}
                onChange={(d) => this.setState({ followupForm: { ...followupForm, date: d } })}
              />
            </Col>
            <Col span={12}>
              <label className={styles.filterLabel}>Time</label>
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                value={followupForm.time}
                onChange={(t) => this.setState({ followupForm: { ...followupForm, time: t } })}
              />
            </Col>
            <Col span={24}>
              <label className={styles.filterLabel}>Remarks</label>
              <Input.TextArea
                rows={3}
                placeholder="What was discussed on this call?"
                value={followupForm.remarks}
                onChange={(e) => this.setState({ followupForm: { ...followupForm, remarks: e.target.value } })}
              />
            </Col>
            <Col span={24}>
              <label className={styles.filterLabel}>Next Followup</label>
              <DatePicker
                style={{ width: "100%" }}
                value={followupForm.next}
                onChange={(d) => this.setState({ followupForm: { ...followupForm, next: d } })}
              />
            </Col>
          </Row>
        </Modal>

        {/* ==================== STATUS DRAWER ==================== */}
        <Drawer
          title="Update Status"
          placement="right"
          width={420}
          visible={statusDrawerVisible}
          onClose={this.closeStatusDrawer}
        >
          {statusLead ? (
            <div>
              <div className={styles.leadSummary}>
                <b>{statusLead.name}</b>
                <div className={styles.mutedText}>
                  {statusLead.phone} · {statusLead.counsellorName}
                </div>
              </div>

              <label className={styles.filterLabel} style={{ marginTop: 16, display: "block" }}>
                Status
              </label>
              <div className={styles.statusPickWrap}>
                {STATUSES.map((s) => (
                  <div
                    key={s}
                    onClick={() => this.pickDrawerStatus(s)}
                    className={styles.statusOpt}
                    style={
                      drawerStatus === s
                        ? { borderColor: STATUS_COLOR[s], background: STATUS_COLOR[s], color: "#fff" }
                        : { borderColor: STATUS_COLOR[s], color: STATUS_COLOR[s] }
                    }
                  >
                    {s}
                  </div>
                ))}
              </div>

              <label className={styles.filterLabel} style={{ marginTop: 16, display: "block" }}>
                Sub Rating
              </label>
              <Select
                style={{ width: "100%" }}
                value={drawerSubRating}
                onChange={(v) => this.setState({ drawerSubRating: v })}
              >
                {(SUB_RATINGS[drawerStatus] || []).map((sr) => (
                  <Option key={sr} value={sr}>
                    {sr}
                  </Option>
                ))}
              </Select>
              <div className={styles.mutedText} style={{ marginTop: 6 }}>
                {(SUB_RATINGS[drawerStatus] || []).length} sub ratings available for "{drawerStatus}".
              </div>

              <label className={styles.filterLabel} style={{ marginTop: 16, display: "block" }}>
                Remarks
              </label>
              <Input.TextArea
                rows={3}
                placeholder="Why is the status changing?"
                value={drawerRemarks}
                onChange={(e) => this.setState({ drawerRemarks: e.target.value })}
              />

              <Button type="primary" style={{ marginTop: 20, width: "100%" }} onClick={this.saveStatusDrawer}>
                Save status
              </Button>
            </div>
          ) : null}
        </Drawer>

        {/* ==================== MOVE MODAL ==================== */}
        <Modal
          title="Move Lead"
          visible={moveVisible}
          onCancel={this.closeMove}
          onOk={this.saveMove}
          okText="Move"
          confirmLoading={moveSaving}
        >
          {moveLead ? (
            <div>
              <p className={styles.mutedText}>Where do you want to move {moveLead.name}?</p>
              <div className={styles.statusPickWrap}>
                <div
                  onClick={() => this.setState({ moveTarget: "inquiry" })}
                  className={styles.statusOpt}
                  style={
                    moveTarget === "inquiry"
                      ? { borderColor: "#2563eb", background: "#2563eb", color: "#fff" }
                      : { borderColor: "#2563eb", color: "#2563eb" }
                  }
                >
                  Inquiry
                </div>
                <div
                  onClick={() => this.setState({ moveTarget: "visitorVisa" })}
                  className={styles.statusOpt}
                  style={
                    moveTarget === "visitorVisa"
                      ? { borderColor: "#7c3aed", background: "#7c3aed", color: "#fff" }
                      : { borderColor: "#7c3aed", color: "#7c3aed" }
                  }
                >
                  Visitor Visa
                </div>
              </div>
              <p className={styles.mutedText} style={{ marginTop: 10 }}>
                The lead will stay in this list, marked as "Moved".
              </p>
            </div>
          ) : null}
        </Modal>
      </PageHeaderWrapper>
    );
  }
}

export default RawInquiryList;
